// TripBook Activation (phases 1 and 2). Mirrors the contract shared by
// 51trips-colab-api, partners-portal and cloud-functions
// (tripbook-activations-endpoint.md, §1 and §3). Timestamps are epoch millis;
// trip dates are epoch millis at UTC midnight, like itinerary.startDate/endDate.

export type ActivationRoute = 'A' | 'B' | 'C1'

export type ActivationState =
    | 'ASSIGNED'
    | 'DELIVERABLE_SENT'
    | 'OPENED'
    | 'CTA_CLICKED'
    | 'CONFIRMED'
    | 'ACTIVATED'
    | 'EXPIRED'
    | 'CANCELLED'

export const FINAL_ACTIVATION_STATES: ActivationState[] = ['ACTIVATED', 'EXPIRED', 'CANCELLED']
export const isFinalActivationState = (state: ActivationState) => FINAL_ACTIVATION_STATES.includes(state)

export type ActivationChannel = 'EMAIL' | 'SMS' | 'WHATSAPP' | 'PHONE'
export type ConsentChannel = 'EMAIL' | 'SMS' | 'WHATSAPP'
export type ContactStatus = 'OK' | 'BOUNCED' | 'INVALID'

// The consent text the agency confirms in the portal. Bump the version (and the
// i18n text under activation.consent.text) together whenever the wording changes.
export const ACTIVATION_CONSENT_TEXT_VERSION = 'activation-consent-2026-11'

export type ActivationConsent = {
    granted: boolean
    channels: ConsentChannel[]
    // Set by the backend; only some reads return them.
    at?: number
    by?: string
    textVersion?: string
}

export type ActivationTraveler = {
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    phone?: string | null
    preferredChannel?: ActivationChannel | null
    noContact: boolean
    contactStatus?: ContactStatus
    // Phone (SMS/WhatsApp) status; contactStatus is the email's (pipelines contract §1.1).
    phoneStatus?: ContactStatus
    consent?: ActivationConsent | null
}

export type ActivationTrip = {
    startDate: number | null
    endDate: number | null
    countries: string[]
    tripId?: string | null
}

// Only the milestones reached are present.
export type ActivationMilestones = Partial<Record<
    'assignedAt' | 'deliverableSentAt' | 'openedAt' | 'ctaAt' | 'confirmedAt' | 'activatedAt' | 'expiredAt' | 'cancelledAt',
    number
>>

export type ActivationNext = {
    touchpointKey: string
    at: number
    channel: ActivationChannel
}

export type ActivationLastTouch = {
    touchpointKey: string
    status: TouchpointStatus
    at: number
}

// GET /activations, GET /activations/{id}
export type ActivationView = {
    id: string
    codeId: string
    code: string
    codeType: 'UNIQUE' | 'GENERIC'
    agencyId: string
    packId: string | null
    route: ActivationRoute
    market: string
    state: ActivationState
    // pausedUntil: epoch millis until which automatic reminders are paused.
    flags: {
        contactFailed: boolean
        pausedUntil?: number | null
        // Admin reads only (pipelines contract §1.1, §3.1).
        contactFailedReason?: string | null
        contactFailedChannel?: ActivationChannel | null
    }
    traveler: ActivationTraveler
    trip: ActivationTrip
    milestones: ActivationMilestones
    redemptionDeadline: number | null
    daysToDeadline: number | null
    next: ActivationNext | null
    lastTouch: ActivationLastTouch | null
    createdAt: number
    // Phase 3 (pipelines contract §6): null on route B.
    crmStage?: string | null
    lostReason?: 'CANCELLED' | 'OPTED_OUT' | 'WRONG_CONTACT' | null
}

export type TouchpointStatus =
    | 'SCHEDULED' | 'SENDING' | 'SENT' | 'DELIVERED' | 'OPENED' | 'CLICKED'
    | 'BOUNCED' | 'FAILED' | 'UNKNOWN' | 'CANCELLED' | 'SKIPPED' | 'SIMULATED'

// §1.2 without internal fields (ruleVersion, attempts).
export type Touchpoint = {
    id?: string
    key: string
    recipient: 'TRAVELER' | 'AGENCY'
    channel: ActivationChannel
    type: 'AUTO' | 'HUMAN'
    scheduledAt: number | null
    sentAt: number | null
    status: TouchpointStatus
    templateId?: string | number | null
    variant?: string | null
    providerMessageId?: string | null
    // Lowercase engine reason (no_opt_in, min_gap, paused, lost,
    // channel_disabled, channel_not_configured, ...); see
    // activation.skipReason.* in the dashboard locale.
    skipReason?: string | null
    createdBy?: string | null
    dueAt?: number | null
    assignee?: string | null
    doneAt?: number | null
    doneBy?: string | null
    outcome?: string | null
    stage?: string | null
}

export type ActivationDetail = ActivationView & {touchpoints: Touchpoint[]}

export type ActivationsPage = {
    items: ActivationView[]
    nextCursor: string | null
}

export type ActivationsQuery = {
    agencyId?: string
    packId?: string
    state?: ActivationState
    notActivated?: boolean
    codeId?: string
    limit?: number
    cursor?: string
}

// GET /activation-policies/{market} (§1.3)
export type ActivationPolicy = {
    market?: string
    requiredContact: 'EMAIL' | 'PHONE'
    preferredContact?: 'EMAIL' | 'PHONE' | null
    travelerChannels?: ActivationChannel[]
    validityMonths?: number
    timeZone?: string
    sendAtLocalTime?: string
    window?: {from: string; to: string}
}

// The `activation` block on POST /invitation-codes/{id}, PUT /invitation-codes
// and (per traveler) POST /invitation-codes/group (§2.1). Without it the
// backend creates no activation. consent.by/at are set server-side.
export type ActivationAssignmentBlock = {
    packId?: string
    noContact: boolean
    preferredChannel?: ActivationChannel
    consent: {granted: boolean; textVersion: string; channels: ConsentChannel[]}
}

// PATCH /activations/{id}/traveler
export type UpdateActivationTravelerPayload = {
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    phone?: string | null
    preferredChannel?: ActivationChannel | null
    noContact: boolean
    consent: {granted: boolean; textVersion: string; channels: ConsentChannel[]}
}

// POST /activations/{id}/commands
export type ActivationCommand = {type: 'CANCEL' | 'REQUEST_EXTRA'; note?: string}

// PATCH /agencies/me/activation-settings
export type AgencyActivationSettings = {
    optInPreTrip: boolean
    activationReportEmail?: string | null
}

// pack.activationCounters on GET /metrics/b2b/.../packs/{packId} (§2.3).
export type ActivationCounters = {
    available: number
    assigned: number
    activated: number
    expiringIn30d: number
    expiredReturned: number
}

// Backend error keys this UI reacts to (MessageErrorDTO.errorKey).
export const ACTIVATION_ERROR_CONTACT_REQUIRED = 'ACTIVATION_CONTACT_REQUIRED'
export const ACTIVATION_ERROR_NO_PACK_UNITS = 'NO_PACK_UNITS'

// The 6-step indicator (assigned → deliverable sent → landing opened → CTA →
// dates confirmed → registered), read from milestones, never from state: a
// skipped step only fills the milestones that actually happened (§1.4).
export const ACTIVATION_STEPS = [
    {milestone: 'assignedAt', labelKey: 'activation.step.assigned'},
    {milestone: 'deliverableSentAt', labelKey: 'activation.step.deliverableSent'},
    {milestone: 'openedAt', labelKey: 'activation.step.opened'},
    {milestone: 'ctaAt', labelKey: 'activation.step.cta'},
    {milestone: 'confirmedAt', labelKey: 'activation.step.confirmed'},
    {milestone: 'activatedAt', labelKey: 'activation.step.activated'},
] as const

// Consent channels the traveler's contact data makes possible: EMAIL with an
// email, SMS + WHATSAPP with a phone. Phase 2 only sends email, but the consent
// text covers messaging too, so it is recorded now for phase 3.
export function consentChannelsFor(email?: string | null, phone?: string | null): ConsentChannel[] {
    const channels: ConsentChannel[] = []
    if (email?.trim()) channels.push('EMAIL')
    if (phone?.trim()) channels.push('SMS', 'WHATSAPP')
    return channels
}
